import { useTranslation } from 'react-i18next'
import { v4 } from 'uuid'

import deleteIcon from '@assets/images/form-delete-icon.svg'
import AddIcon from '@mui/icons-material/AddRounded'

import {
  ArrayColModel,
  ArrayFieldModel,
  CheckboxFieldModel,
  DateFieldModel,
  FieldType,
  FormField,
  GridTextFieldModel,
  RadioFieldModel,
  SelectEditableFieldModel,
  SelectFieldModel,
  SignatureFieldModel,
  TextFieldModel,
} from '@types'

import { SubmitterFieldCheckbox } from '../SubmitterFieldCheckbox'
import { SubmitterFieldDate } from '../SubmitterFieldDate'
import { SubmitterFieldRadio } from '../SubmitterFieldRadio'
import { SubmitterFieldSelect } from '../SubmitterFieldSelect'
import { SubmitterFieldSelectEditable } from '../SubmitterFieldSelectEditable'
import { SubmitterFieldSignature } from '../SubmitterFieldSignature'
import { SubmitterFieldText } from '../SubmitterFieldText'

import { resetFieldValues } from '../../../../../project-form-models/components/FormBuilder/FormBuilder.helper'

import {
  AddButton,
  ColHeader,
  Cols,
  ColsContainer,
  Container,
  DeleteIcon,
  Field,
  Label,
} from './SubmitterFieldArray.styled'
import { SubmitterFieldGridText } from '../SubmitterFieldGridText'

type SubmitterFieldArrayProps = {
  field: ArrayFieldModel
  disabled?: boolean
  isPreview?: boolean
  scrollTo: (id: string) => void
  onChange: (updates: Partial<ArrayFieldModel>) => void
}

export const SubmitterFieldArray: React.FC<SubmitterFieldArrayProps> = ({
  field: arrayField,
  disabled = false,
  isPreview = false,
  scrollTo,
  onChange,
}: SubmitterFieldArrayProps) => {
  const { t } = useTranslation()

  const renderField = (field: FormField) => {
    const commonProps = {
      onChange: (value: Partial<FormField>) => {
        const updatedCols = arrayField.cols.map((col) => {
          const updatedFields = col.fields.map((colField) =>
            colField.id === field.id ? { ...colField, ...value } : colField,
          )
          return { ...col, fields: updatedFields }
        })

        onChange({ cols: updatedCols as ArrayColModel[] })
      },
      disabled,
    }

    const optionsProps = {
      ...commonProps,
      onRedirect: scrollTo,
    }

    switch (field.type) {
      case FieldType.Text:
        return <SubmitterFieldText field={field as TextFieldModel} {...commonProps} />
      case FieldType.Checkbox:
        return <SubmitterFieldCheckbox field={field as CheckboxFieldModel} {...commonProps} />
      case FieldType.Radio:
        return <SubmitterFieldRadio field={field as RadioFieldModel} {...optionsProps} />
      case FieldType.Select:
        return <SubmitterFieldSelect field={field as SelectFieldModel} {...optionsProps} />
      case FieldType.SelectEditable:
        return <SubmitterFieldSelectEditable field={field as SelectEditableFieldModel} {...optionsProps} />
      case FieldType.Date:
        return <SubmitterFieldDate field={field as DateFieldModel} {...commonProps} />
      case FieldType.Signature:
        return <SubmitterFieldSignature field={field as SignatureFieldModel} {...commonProps} />
      case FieldType.GridText:
        return <SubmitterFieldGridText field={field as GridTextFieldModel} {...commonProps} isPreview={isPreview} />
      default:
        return null
    }
  }

  const handleAddCol = () => {
    const lastCol = arrayField.cols[arrayField.cols.length - 1]
    const newCol = {
      id: v4(),
      fields: lastCol.fields.map((field) => resetFieldValues(field)),
    }

    onChange({ cols: [...arrayField.cols, newCol] })
  }

  const handleRemoveCol = (colId: string) => {
    const updatedCols = arrayField.cols.filter((col) => col.id !== colId)
    onChange({ cols: updatedCols })
  }

  const canRemoveCol = arrayField.cols.length > 1 && !disabled

  return (
    <Container>
      <Label>{arrayField.label}</Label>
      <ColsContainer>
        {arrayField.cols.map((col) => (
          <Cols key={col.id}>
            <ColHeader>
              {canRemoveCol && <DeleteIcon onClick={() => handleRemoveCol(col.id)} src={deleteIcon} />}
            </ColHeader>
            {col.fields.map((field) => (
              <Field key={field.id}>{renderField(field)}</Field>
            ))}
          </Cols>
        ))}
      </ColsContainer>

      {!disabled && (
        <AddButton type="button" onClick={handleAddCol}>
          <AddIcon />
          {t('modules_project-forms_components_FormSubmitter_components_SubmitterFieldArray_add_label')}
        </AddButton>
      )}
    </Container>
  )
}
